import React, { useState } from "react";
import { Error, Loader } from "../";
import { API, endpoints } from "../../api";
import { errorToString } from "../../utils/utils";
// import { GoogleBtn } from "./google-btn";
import { Password } from "./password";
import { useAuth } from "../../context/Auth";
import LocationPicker from "../location-picker";
import { CountryPhoneInput } from "../country-phone-input";
import { OtpModal } from "./otp-modal";
import { toastType } from "../../utils/strings";
import { isValidPhoneNumber } from "libphonenumber-js";
import { MOBILE_NUMBER_REQUIRED } from "../../constants";

// Names may only contain letters, spaces, hyphens and apostrophes.
const NAME_ALLOWED = /^[\p{L}][\p{L} '-]*$/u;
const stripNameSymbols = (value) => value.replace(/[^\p{L} '-]/gu, "");

export const SignUp = ({ changeForm }) => {
  const [firstName, updateFirstName] = useState("");
  const [lastName, updateLastName] = useState("");
  const [email, updateEmail] = useState("");
  const [password, updatePassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [address, setAddress] = useState({});
  
  // Mobile number states
  const [mobileNumber, setMobileNumber] = useState("");
  const [mobileCode, setMobileCode] = useState("");
  const [mobileCountryCode, setMobileCountryCode] = useState("");
  
  // OTP modal state
  const [showOtpModal, setShowOtpModal] = useState(false);

  const { updateUser, showToast, setOtpModalOpen } = useAuth();

  const buildE164 = (code, number) => {
    const cleanCode = (code || "").toString().trim();
    const cleanNumber = (number || "").toString().replace(/[^0-9]/g, "").replace(/^0+/, "");
    if (!cleanCode || !cleanNumber) return "";
    return `${cleanCode}${cleanNumber}`;
  };

  const isValidMobile = (code, number) => {
    const fullNumber = buildE164(code, number);
    if (!fullNumber) return false;
    try {
      return isValidPhoneNumber(fullNumber);
    } catch (_e) {
      return false;
    }
  };

  const doSignUp = (event) => {
    event.preventDefault();
    if (firstName && lastName && email && password) {

      if (!NAME_ALLOWED.test(firstName.trim()) || !NAME_ALLOWED.test(lastName.trim())) {
        setError("Names can only contain letters, spaces, hyphens and apostrophes");
        return;
      }

      const hasSelectedAddress = Boolean(
        address?.postal_address1 && address?.country && address?.latitude
      );

      if (!hasSelectedAddress) {
        setError("Please select an address from the drop-down list");
        return;
      }

      const sanitisedMobileNumber = mobileNumber.replace(/[^0-9]/g, "").replace(/^0+/, "");
      const hasMobileNumber = Boolean(sanitisedMobileNumber && mobileCode && mobileCountryCode);

      if (MOBILE_NUMBER_REQUIRED && !hasMobileNumber) {
        setError("Please enter a valid mobile number with country code");
        return;
      }

      if (hasMobileNumber && !isValidMobile(mobileCode, sanitisedMobileNumber)) {
        setError("Please enter a valid mobile number with country code");
        return;
      }

      setLoading(true);

      var data = new FormData();
      data.append("first_name", firstName);
      data.append("last_name", lastName);
      data.append("name", `${firstName} ${lastName}`);
      data.append("email", email);
      data.append("password", password);
      
      // Add mobile number fields
      data.append("mobile_number", sanitisedMobileNumber);
      data.append("mobile_code", mobileCode);
      data.append("mobile_country_code", mobileCountryCode);

      Object.keys(address).forEach((key) => {
        data.append(key, address[key]);
      });

      setError("");
      API.post({ route: endpoints.REGISTER, data })
        .then((response) => {
          // Display the OTP from response for testing
          if (response?.data?.otp) {
            showToast({
              text: `OTP for testing: ${response.data.otp}`,
              type: toastType.INFO,
            });
          }
          
          // Update user in context
          updateUser(response);

          if (MOBILE_NUMBER_REQUIRED && hasMobileNumber) {
            setOtpModalOpen(true);
            setShowOtpModal(true);
          }
          
          showToast({
            text: MOBILE_NUMBER_REQUIRED && hasMobileNumber
              ? "Registration successful! Please verify your mobile number."
              : "Registration successful!",
            type: toastType.SUCCESS,
          });
        })
        .catch((data) => {
          setError(errorToString(data?.response));
        })
        .finally(() => setLoading(false));
    } else {
      setError("Please fill all fields");
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <form
        onSubmit={doSignUp}
        className="card shadow-none border-0 ms-auto me-auto"
      >
        <div className="card-body rounded-0 text-left">
          <h3 className="fw-700 display1-size display2-md-size mb-4 text-center">
            SIGN UP
          </h3>

          <Error error={error} />

          <div className="form-group icon-input mb-3">
            <i className="font-sm ti-user text-grey-500 pe-0" />
            <input
              type="text"
              value={firstName}
              autocorrect="off" autocapitalize="none"
              onChange={(event) => updateFirstName(stripNameSymbols(event.target.value))}
              className="style2-input ps-5 form-control text-grey-900 font-xsss fw-600"
              placeholder="Legal First Name"
              required
            />
          </div>
          <div className="form-group icon-input mb-3">
            <i className="font-sm ti-user text-grey-500 pe-0" />
            <input
              type="text"
              value={lastName}
              onChange={(event) => updateLastName(stripNameSymbols(event.target.value))}
              className="style2-input ps-5 form-control text-grey-900 font-xsss fw-600"
              placeholder="Legal Last Name"
              required
            />
          </div>
          <div className="form-group icon-input mb-3">
            <i className="font-sm ti-email text-grey-500 pe-0" />
            <input
              value={email}
              onChange={(event) => updateEmail(event.target.value)}
              type="text"
              className="style2-input ps-5 form-control text-grey-900 font-xsss fw-600"
              placeholder="Email Address"
            />
          </div>
          <Password value={password} update={updatePassword} />

          <LocationPicker data={address} canShowFullForm={false} setAddress={setAddress} customIconStyle={{ fontSize: 12, marginTop: 4 }} />

          
          {/* Mobile Number with Country Code */}
          <div className="form-group mb-3 notranslate">
            <label className="mont-font fw-600 font-xsss mb-2">
              Mobile Number with Country Code {MOBILE_NUMBER_REQUIRED ? <span className="text-red">*</span> : null}
            </label>
            <CountryPhoneInput
              value={{
                mobile_number: mobileNumber,
                mobile_code: mobileCode,
                mobile_country_code: mobileCountryCode
              }}
              onChange={(next) => {
                setMobileNumber(next?.mobile_number || "");
                setMobileCountryCode(next?.mobile_country_code || "");
                setMobileCode(next?.mobile_code || "");
              }}
              defaultCountryCode="in"
              numberPlaceholder="Phone Number"
            />
          </div>
          
          <div className="col-sm-12 p-0 text-left">
            <div className="form-group mb-1">
              <button
                type="submit"
                className="form-control text-center style2-input text-white fw-600 bg-primary-gradiant border-0 p-0"
              >
                {MOBILE_NUMBER_REQUIRED ? "VERIFY" : "SIGN UP"}
              </button>
            </div>
            <h6 className="text-grey-500 font-xsss fw-500 mt-4 mb-2 lh-32 text-center cursor-pointer">
              <button
                type="button"
                onClick={() => changeForm(true)}
                className="fw-700 ms-1 text-center bg-transparent border-0 p-0"
                style={{ cursor: 'pointer', color: 'inherit' }}
              >
                Already a Member? Login
              </button>
            </h6>
          </div>
          {/* <GoogleBtn /> */}
      {MOBILE_NUMBER_REQUIRED ? <p className="font-xsss"><span>*</span> Mobile number verification protects your account</p> : null}

        </div>
      </form>

      
      {/* OTP Verification Modal */}
      <OtpModal 
        show={showOtpModal} 
        close={() => {
          setShowOtpModal(false);
          setOtpModalOpen(false);
        }}
        mobileNumber={mobileNumber}
        mobileCode={mobileCode}
        mobileCountryCode={mobileCountryCode}
        btnText={"VERIFY"}
        onVerified={() => {
          // Handle successful verification
          showToast({
            text: "Your account has been verified successfully!",
            type: toastType.SUCCESS,
          });
          setOtpModalOpen(false);
        }}
      />
    </>
  );
};
